import { useState } from 'react';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';
import { X, Clock, CheckCircle2, PlayCircle, MessageSquare, Send, CheckSquare } from 'lucide-react';
import { checklistInstancesApi, checklistSubtasksApi, ChecklistItemInstance, ChecklistItemSubtask } from '../lib/api';
import { Card } from './ui/Card';
import { Button } from './ui/Button';
import { Badge } from './ui/Badge';
import { useTelegram } from '../hooks/useTelegram';

interface ChecklistInstanceModalProps {
  instance: ChecklistItemInstance;
  onClose: () => void;
}

export function ChecklistInstanceModal({ instance, onClose }: ChecklistInstanceModalProps) {
  const { hapticFeedback, showAlert } = useTelegram();
  const queryClient = useQueryClient();
  const [commentText, setCommentText] = useState('');

  const statusMutation = useMutation({
    mutationFn: (status: 'IN_PROGRESS' | 'COMPLETED') =>
      checklistInstancesApi.updateStatus(instance.id, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['checklist-instances'] });
      queryClient.invalidateQueries({ queryKey: ['checklist-plan'] });
      hapticFeedback('success');
      showAlert('Статус обновлен');
    },
    onError: (error: any) => {
      hapticFeedback('error');
      showAlert(error.message || 'Не удалось обновить статус');
    },
  });

  const commentMutation = useMutation({
    mutationFn: (text: string) => checklistInstancesApi.addComment(instance.id, text),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['checklist-instances', instance.id] });
      setCommentText('');
      hapticFeedback('success');
      showAlert('Комментарий добавлен');
    },
    onError: (error: any) => {
      hapticFeedback('error');
      showAlert(error.message || 'Не удалось добавить комментарий');
    },
  });

  const handleStatusChange = (status: 'IN_PROGRESS' | 'COMPLETED') => {
    hapticFeedback('medium');
    statusMutation.mutate(status);
  };

  const handleAddComment = () => {
    if (!commentText.trim()) {
      showAlert('Введите комментарий');
      return;
    }
    hapticFeedback('medium');
    commentMutation.mutate(commentText.trim());
  };

  const statusConfig = {
    ASSIGNED: { label: 'Назначена', badge: 'default' as const },
    IN_PROGRESS: { label: 'В работе', badge: 'info' as const },
    COMPLETED: { label: 'Выполнена', badge: 'success' as const },
    COMPLETED_PENDING: { label: 'Ожидает приёмки', badge: 'warning' as const },
    ACCEPTED: { label: 'Принята', badge: 'success' as const },
    REJECTED: { label: 'Отклонена', badge: 'danger' as const },
  };

  const config = statusConfig[instance.status];
  const isOverdue = new Date() > new Date(instance.dueAt) && 
                   instance.status !== 'ACCEPTED' && 
                   instance.status !== 'COMPLETED';

  // Загружаем чек-пункты для правила задачи
  const { data: subtasks = [] } = useQuery<ChecklistItemSubtask[]>({
    queryKey: ['checklist-subtasks', instance.ruleId],
    queryFn: () => checklistSubtasksApi.list(instance.ruleId),
    enabled: !!instance.ruleId,
  });

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-tg-text">Дело чек-листа</h2>
          <Button
            size="sm"
            variant="secondary"
            onClick={onClose}
          >
            <X className="w-5 h-5" />
          </Button>
        </div>

        <div className="space-y-4">
          {/* Информация о деле */}
          <div>
            <h3 className="font-semibold text-tg-text mb-2">{instance.rule?.text}</h3>
            <div className="flex items-center gap-2 mb-2">
              <Badge variant={config.badge}>{config.label}</Badge>
              {isOverdue && <Badge variant="danger">Просрочено</Badge>}
              {instance.rule?.recurrence && (
                <Badge variant="default">
                  {instance.rule.recurrence === 'DAILY' && 'Ежедневно'}
                  {instance.rule.recurrence === 'WEEKLY' && 'Еженедельно'}
                  {instance.rule.recurrence === 'MONTHLY' && 'Ежемесячно'}
                  {instance.rule.recurrence === 'ONCE' && 'Один раз'}
                </Badge>
              )}
            </div>
            <div className="space-y-1 text-sm text-tg-hint">
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                <span>
                  Дедлайн: {format(new Date(instance.dueAt), 'dd.MM.yyyy HH:mm', { locale: ru })}
                </span>
              </div>
              {instance.completedAt && (
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4" />
                  <span>
                    Выполнено: {format(new Date(instance.completedAt), 'dd.MM.yyyy HH:mm', { locale: ru })}
                  </span>
                </div>
              )}
              {instance.acceptedAt && (
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-green-500" />
                  <span>
                    Принято: {format(new Date(instance.acceptedAt), 'dd.MM.yyyy HH:mm', { locale: ru })}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Чек-пункты */}
          {subtasks.length > 0 && (
            <div>
              <h4 className="font-medium text-tg-text mb-2 flex items-center gap-2">
                <CheckSquare className="w-4 h-4" />
                Чек-пункты
              </h4>
              <div className="space-y-1">
                {subtasks.map((subtask) => (
                  <div key={subtask.id} className="flex items-start gap-2 p-2 bg-gray-50 rounded text-sm">
                    <span className="text-blue-500 mt-0.5">•</span>
                    <span className="text-tg-text flex-1">{subtask.text}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Комментарии */}
          {instance.comments && instance.comments.length > 0 && (
            <div>
              <h4 className="font-medium text-tg-text mb-2 flex items-center gap-2">
                <MessageSquare className="w-4 h-4" />
                Комментарии
              </h4>
              <div className="space-y-2">
                {[...instance.comments]
                  .sort(
                    (a, b) =>
                      new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
                  )
                  .map((comment) => (
                    <div key={comment.id} className="p-2 bg-gray-50 rounded text-sm">
                      <div className="flex items-center justify-between gap-2">
                        <div className="font-medium text-tg-text truncate">
                          {comment.user?.firstName} {comment.user?.lastName}
                        </div>
                        <div className="text-xs text-tg-hint whitespace-nowrap">
                          {format(new Date(comment.createdAt), 'dd.MM.yyyy HH:mm', {
                            locale: ru,
                          })}
                        </div>
                      </div>
                      <div className="text-tg-hint mt-1 whitespace-pre-wrap break-words">
                        {comment.text}
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          )}

          {/* Добавить комментарий */}
          <div>
            <h4 className="font-medium text-tg-text mb-2">Добавить комментарий</h4>
            <div className="flex gap-2">
              <textarea
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
                placeholder="Введите комментарий..."
                className="flex-1 p-2 border rounded-lg text-sm"
                rows={2}
              />
              <Button
                size="sm"
                variant="primary"
                onClick={handleAddComment}
                disabled={!commentText.trim() || commentMutation.isPending}
              >
                <Send className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* Действия */}
          {instance.status === 'ASSIGNED' && (
            <div className="flex gap-2 pt-4 border-t">
              <Button
                variant="secondary"
                onClick={() => handleStatusChange('IN_PROGRESS')}
                disabled={statusMutation.isPending}
                className="flex-1"
              >
                <PlayCircle className="w-4 h-4 mr-1" />
                В процессе
              </Button>
              <Button
                variant="primary"
                onClick={() => handleStatusChange('COMPLETED')}
                disabled={statusMutation.isPending}
                className="flex-1"
              >
                <CheckCircle2 className="w-4 h-4 mr-1" />
                Выполнено
              </Button>
            </div>
          )}

          {instance.rejectionComment && (
            <div className="p-3 bg-red-50 rounded-lg">
              <div className="font-medium text-red-600 mb-1">Причина отклонения:</div>
              <div className="text-sm text-red-700">{instance.rejectionComment}</div>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}

